import { createFileRoute } from '@tanstack/react-router'
import { Bell, Loader2 } from 'lucide-react'

import {
  useSystemNotifications,
  useUpdateSystemNotifications,
  NOTIFICATION_TYPE_LABELS,
  type NotificationSettingsType as NotificationType,
  type NotificationSettingsChannel as NotificationChannel,
} from '@/hooks/notifications'

export const Route = createFileRoute('/_authenticated/settings/notifications' as never)({
  component: SystemNotificationsPage,
})

function SystemNotificationsPage() {
  const { data: settings, isLoading, error } = useSystemNotifications()
  const updateSettings = useUpdateSystemNotifications()

  if (isLoading) {
    return (
      <div className="flex h-64 items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    )
  }

  if (error) {
    return (
      <div className="flex h-64 items-center justify-center">
        <p className="text-destructive">加载设置失败: {error.message}</p>
      </div>
    )
  }

  const handleChannelToggle = (channel: NotificationChannel, enabled: boolean) => {
    updateSettings.mutate({ channels: { [channel]: { enabled } } })
  }

  const handleTriggerToggle = (type: NotificationType, enabled: boolean) => {
    updateSettings.mutate({ triggers: { [type]: { enabled } } })
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <div className="inline-flex h-12 w-12 items-center justify-center rounded-lg bg-orange-500">
          <Bell className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-2xl font-bold tracking-tight">系统通知设置</h1>
          <p className="text-muted-foreground">配置系统级通知渠道和触发器</p>
        </div>
      </div>

      <div className="grid gap-6 md:grid-cols-2">
        {/* 通知渠道 */}
        <div className="rounded-lg border bg-card">
          <div className="border-b p-4">
            <h2 className="font-semibold">通知渠道</h2>
            <p className="text-sm text-muted-foreground">启用或禁用系统通知渠道</p>
          </div>
          <div className="p-4 space-y-4">
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="text-sm font-medium">邮件通知</p>
                <p className="text-xs text-muted-foreground">通过邮件发送通知</p>
              </div>
              <ToggleSwitch
                checked={settings?.channels.email.enabled ?? false}
                onChange={(checked) => handleChannelToggle('email', checked)}
                disabled={updateSettings.isPending}
              />
            </div>
            <div className="flex items-center justify-between rounded-lg border p-4">
              <div>
                <p className="text-sm font-medium">站内通知</p>
                <p className="text-xs text-muted-foreground">在系统内显示通知</p>
              </div>
              <ToggleSwitch
                checked={settings?.channels.inApp.enabled ?? false}
                onChange={(checked) => handleChannelToggle('inApp', checked)}
                disabled={updateSettings.isPending}
              />
            </div>
          </div>
        </div>

        {/* 通知触发器 */}
        <div className="rounded-lg border bg-card">
          <div className="border-b p-4">
            <h2 className="font-semibold">通知触发器</h2>
            <p className="text-sm text-muted-foreground">配置哪些事件触发通知</p>
          </div>
          <div className="p-4 space-y-4">
            {settings && Object.entries(settings.triggers).map(([type, config]) => (
              <div key={type} className="flex items-center justify-between rounded-lg border p-4">
                <div>
                  <p className="text-sm font-medium">
                    {NOTIFICATION_TYPE_LABELS[type as NotificationType]}
                  </p>
                </div>
                <ToggleSwitch
                  checked={config.enabled}
                  onChange={(checked) => handleTriggerToggle(type as NotificationType, checked)}
                  disabled={updateSettings.isPending}
                />
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  )
}

interface ToggleSwitchProps {
  checked: boolean
  onChange: (checked: boolean) => void
  disabled?: boolean
}

function ToggleSwitch({ checked, onChange, disabled }: ToggleSwitchProps) {
  return (
    <button
      onClick={() => onChange(!checked)}
      disabled={disabled}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${checked ? 'bg-primary' : 'bg-muted'
        } ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}
    >
      <span
        className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${checked ? 'translate-x-6' : 'translate-x-1'
          }`}
      />
    </button>
  )
}
